import React, { useState } from "react";
import _ from "lodash";
import { makeStyles, useTheme } from "@mui/styles";
import { formatDate } from "features/helpers/utils";
import { useFetchTeamMember } from "../redux/hooks";
import Channel from "./Channel";
import TicketStatus from "./TicketStatus";
import { getUserAvatarIcon } from "../utils";
import { getShortAddress } from "features/helpers/utils";
import { useConnectWallet } from "features/home/redux/connectWallet";
import Comment from "./Comment";
import TicketTypeLabel from "./TicketTypeLabel";
import CRMTable from "@metacrm/metacrm-material-ui/dist/Table";
import { Box } from "@mui/material";
import TextWithTooltip from "components/Text/TextWithTooltip";
import { useIntl } from "react-intl";
const useStyles = makeStyles((theme) => ({
  timestampLg: {
    fontSize: 12,
    color: "#555555",
    fontWeight: 500,
    minWidth: 110,
  },
}));
const kMapping = {
  "Created Time": "created",
  "Last Updated": "lastUserResponse",
  "Last Message": "lastMessage.message",
  Assignee: "team.name",
  Status: "status",
  "Issue Tag": "type",
  "Ticket Title": "title",
  Channel: "from",
  Address: "user.address",
  Comment: "comment",
};

const invertedMapping = _.invert(kMapping);
const TicketTable = ({
  ticketList,
  setOpenTicketDetail,
  entityName,
  containerStyle = {},
  noDataTitle,
  noDataComponent,
  orderData = { order: -1, sort: "created" },
  setOrderData = () => {},
  setPage,
  page,
  dataCounts,
  loading,
  loadingIndicatorAmount = 20,
  rowsPerPage = 20,
  isUserInfoPage,
}) => {
  const { formatMessage } = useIntl();
  const theme = useTheme();
  const classes = useStyles();
  const { socket, userData } = useConnectWallet();
  const { memberList } = useFetchTeamMember();
  const [commentMapping, setCommentMapping] = useState({});
  const isNewMessage = (ticket) => {
    let hasNewMessage = _.get(ticket, "hasNewMessage", false);
    if (hasNewMessage) {
      return true;
    }
    return false;
  };

  const handleReadMessage = (ticketId) => {
    const ticket = _.find(ticketList, { _id: ticketId });
    if (
      socket &&
      _.get(userData, "address") == _.get(ticket, "team.address", "N/A")
    ) {
      socket.emit("adminReadTicket", {
        teamId: _.get(userData, "team._id"),
        ticketId: ticketId,
        entityName,
      });
    }
  };

  const handleSort = (e, property) => {
    const by = kMapping[property];
    const dir = orderData.order > 0 ? -1 : 1;

    setOrderData({ sort: by, order: dir });
  };

  const getRowValueMap = () => {
    const baseMap = {
      [formatMessage({ id: "global.headline.status" })]: (ticket) => (
        <Box slot="Status">
          <TicketStatus status={ticket.status} />
        </Box>
      ),
      [formatMessage({ id: "tickets.headline.title" })]: (ticket) => {
        const isNew = isNewMessage(ticket);
        return (
          <Box slot="Ticket Title">
            <TextWithTooltip
              style={{ fontWeight: 500, fontSize: 12, color: "#222" }}
              data={
                <Box>
                  {isNew && (
                    <span
                      style={{
                        width: "6px",
                        height: "6px",
                        display: "inline-block",
                        background: "#FC5555",
                        borderRadius: 40,
                        marginRight: "8px",
                        verticalAlign: "middle",
                        position: "relative",
                        top: "-1px",
                      }}
                    />
                  )}
                  {_.get(ticket, "title", "")}
                </Box>
              }
              limitRows={1}
            />
          </Box>
        );
      },
      [formatMessage({ id: "tickets.headline.issue" })]: (ticket) => (
        <Box slot="Issue Tag">
          <TicketTypeLabel ticketType={ticket.type} />
        </Box>
      ),
      [formatMessage({ id: "global.headline.lastUpdated" })]: (ticket) => (
        <Box slot="Last Updated" className={classes.timestampLg}>
          {formatDate(_.get(ticket, "lastUserResponse", ""), { time: true })}
        </Box>
      ),
      [formatMessage({ id: "tickets.headline.assignee" })]: (ticket) => {
        const assignee = _.get(ticket, "team.name");
        const isAi = ticket?.isAiChat || false;
        const icon = getUserAvatarIcon(_.get(ticket, "team._id"), theme);
        return (
          <Box
            slot="Assignee"
            className="startRow"
            style={{ flexWrap: "nowrap" }}
            fontWeight={500}
          >
            {isAi ? (
              <Box width={"78px"}>
                <img
                  src={
                    isAi
                      ? require("assets/img/ic_ai.svg").default
                      : require(`assets/img/${icon}`)
                  }
                  className="smIcon"
                  style={{
                    borderRadius: 20,
                    margin: "0px",
                    marginRight: "4px",
                  }}
                />
                AI Agent
              </Box>
            ) : assignee && assignee !== "AI Agent" ? (
              <>
                <img
                  src={require(`assets/img/${icon}`)}
                  className="smIcon"
                  style={{
                    borderRadius: 20,
                    margin: "0px",
                    marginRight: "4px",
                  }}
                />
                {assignee}
              </>
            ) : (
              <>
                <img
                  src={require(`assets/img/ic_no-agent.svg`).default}
                  className="smIcon"
                  style={{
                    borderRadius: 20,
                    margin: "0px",
                    marginRight: "4px",
                  }}
                />
                <span style={{ color: "#A5A5A5" }}>Agent Needed</span>
              </>
            )}
          </Box>
        );
      },
    };

    const userInfoOnlyMap = {
      [formatMessage({ id: "global.commonWord.walletAddress" })]: (ticket) => (
        <Box slot="Wallet Address">
          {getShortAddress(ticket?.userWallet?.address)}
        </Box>
      ),
    };

    const fullTicketMap = {
      [formatMessage({ id: "tickets.headline.reporter" })]: (ticket) => {
        const address =
          ticket?.submitHandle || _.get(ticket, "userWallet.address", "");
        const icon = getUserAvatarIcon(address, theme);
        const renderAddress = () => {
          if (getShortAddress(address) === "-") {
            return _.get(ticket, "email", "") || "-";
          }
          return getShortAddress(address);
        };
        return (
          <Box
            slot="Reporter"
            className="startRow"
            style={{
              fontWeight: 400,
              fontSize: 12,
              flexWrap: "nowrap",
            }}
          >
            {_.get(ticket, "userWallet.address", "") && (
              <img
                src={require(`assets/img/${icon}`)}
                className="smIcon"
                style={{ borderRadius: 20, marginRight: "4px" }}
              />
            )}
            <span>{renderAddress()}</span>
          </Box>
        );
      },
      [formatMessage({ id: "tickets.headline.channel" })]: (ticket) => (
        <Box slot="Channel">
          <Channel ticket={ticket} />
        </Box>
      ),
      [formatMessage({ id: "global.headline.createdTime" })]: (ticket) => (
        <Box slot="Created Time" className={classes.timestampLg}>
          {formatDate(_.get(ticket, "created", ""), { time: true })}
        </Box>
      ),
      [formatMessage({ id: "tickets.headline.comment" })]: (ticket) => {
        if (!ticket.comment && !commentMapping[ticket._id])
          return (
            <Box slot="Comment">
              <Comment
                entityName={entityName}
                selectedTicket={ticket}
                row={true}
                comment={commentMapping[ticket._id]}
                setComment={(v) =>
                  setCommentMapping({
                    ...commentMapping,
                    [ticket._id]: v,
                  })
                }
              />
            </Box>
          );
        return (
          <Box slot="Comment">
            {_.truncate(commentMapping[ticket._id] || ticket.comment)}
          </Box>
        );
      },
    };

    return {
      ...baseMap,
      ...(isUserInfoPage ? userInfoOnlyMap : fullTicketMap),
    };
  };

  const rowValueMap = getRowValueMap(isUserInfoPage);

  const headers = () => {
    const commonFields = [
      {
        show: true,
        disabledSort: false,
        id: "Status",
        label: formatMessage({ id: "global.headline.status" }),
        width: 82,
        fixed: !isUserInfoPage,
      },
      {
        show: true,
        disabledSort: true,
        id: "Ticket Title",
        label: formatMessage({ id: "tickets.headline.title" }),
        width: 220,
        fixed: !isUserInfoPage,
      },
      {
        show: true,
        disabledSort: true,
        id: "Issue Tag",
        label: formatMessage({ id: "tickets.headline.issue" }),
        width: 120,
      },
    ];

    const userInfoFields = [
      {
        show: true,
        disabledSort: true,
        id: "Wallet Address",
        label: formatMessage({ id: "global.commonWord.walletAddress" }),
        width: 120,
      },
      {
        show: true,
        disabledSort: false,
        id: "Last Updated",
        width: 135,
        label: formatMessage({ id: "global.headline.lastUpdated" }),
      },
      {
        show: true,
        disabledSort: false,
        id: "Assignee",
        label: formatMessage({ id: "tickets.headline.assignee" }),
      },
    ];

    const ticketInfoFields = [
      {
        show: true,
        disabledSort: true,
        id: "Reporter",
        label: formatMessage({ id: "tickets.headline.reporter" }),
      },
      {
        show: true,
        disabledSort: true,
        id: "Channel",
        label: formatMessage({ id: "tickets.headline.channel" }),
      },
      {
        show: true,
        disabledSort: false,
        id: "Last Updated",
        width: 135,
        label: formatMessage({ id: "global.headline.lastUpdated" }),
      },
      {
        show: true,
        disabledSort: false,
        id: "Created Time",
        width: 135,
        label: formatMessage({ id: "global.headline.createdTime" }),
      },
      {
        show: true,
        disabledSort: false,
        id: "Assignee",
        label: formatMessage({ id: "tickets.headline.assignee" }),
      },
      {
        show: true,
        disabledSort: false,
        id: "Comment",
        label: formatMessage({ id: "tickets.headline.comment" }),
      },
    ];

    return [
      ...commonFields,
      ...(isUserInfoPage ? userInfoFields : ticketInfoFields),
    ];
  };

  return (
    <Box className="general-shadow">
      <CRMTable
        containerStyle={containerStyle}
        headers={headers()}
        onRequestSort={handleSort}
        orderBy={invertedMapping[orderData.sort]}
        order={orderData.order > 0 ? "asc" : "desc"}
        noDataComponent={noDataComponent}
        onRowClick={(ticketId) => {
          handleReadMessage(ticketId);
          setOpenTicketDetail(ticketId);
        }}
        loadingIndicatorAmount={loadingIndicatorAmount}
        rowsPerPage={rowsPerPage}
        noDataTitle={noDataTitle}
        rowData={ticketList}
        contents={_.map(ticketList, (ticket) => {
          return _.map(headers(), (header) =>
            rowValueMap[header.label](ticket)
          );
        })}
        dataCounts={dataCounts}
        page={page}
        setPage={setPage}
        isLoading={loading}
        pageText={formatMessage({ id: "global.pagination.page" })}
        pageOfText={formatMessage({ id: "global.pagination.of" })}
      />
    </Box>
  );
};

export default TicketTable;
